import React, {useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoading, usePersistedTransactionId } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer, SingleLineFormItemWrapper,
} from '../../components';
import AliPayPlusPaymentImage from '../../resource/figma-images/alipay-plus-payment-image.png';
import ContinueToPayImage from '../../resource/figma-images/continue-to-pay-image.svg';
import CreditCardPaymentImage from '../../resource/figma-images/credit-card-payment-image.svg';
import { TillPaymentInlineForm } from './TillPaymentInlineForm';
import { Alert, Stack, styled } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getCreditCardPaymentImage,
  getDefaultPaymentMethod,
  getPageHeroLogo,
  getPageTitle,
  getThemeColor, shouldHideAlipayPlus,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import { hexToRGBA } from "../../utils/hexToRGBA";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import {PAYMENT_METHOD_ALIPAY, PAYMENT_METHOD_CREDIT_CARD, PAYMENT_METHOD_INITIAL} from "../constants";
import {PrivacyPolicy} from "../../components/PrivacyPolicy";


const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const PaymentFormPageTitle = styled('div')`
  font-family: "Jakarta", "sans-serif";
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #747F94;
`;

const PaymentOptionList = styled('div')`
  display: flex;
  justify-content: space-between;
  // Should not use overflow: scroll because some of the browsers will have a disabled scroll bar forever.
`;

const PaymentOption = styled('div', {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'borderColor'
})(({ selected, borderColor }) => ({
  border: selected ? `2px solid ${borderColor}` : 'none',
  padding: '10px 20px',
  flexGrow: 1,
  flexBasis: '10%',
  height: '90px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  borderRadius: '10px',
  boxShadow: `1px 3px 4px 0px ${selected ? hexToRGBA(borderColor, 0.2) : 'rgba(0, 0, 0, 0.10)'}`,
  background: '#FFF',
  marginRight: '12px',
  '&:last-child': {
    marginRight: '0px'
  }
}));

const PaymentOptionImage = styled('img')`
  height: 30px;
  margin-right: auto;
`;

const PaymentOptionText = styled('div')`
  font-family: "Jakarta", "sans-serif";
  color: #10253E;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 600;
`;

const PaymentDetails = styled('div')`
  padding: 20px 0;
`;

const PaymentHiddenWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'show' // Prevent 'show' from being passed to DOM
})(({ show }) => ({
  display: show ? 'block' : 'none',
  textAlign: 'center',
}));

const PaymentContent = styled('div')`
  font-family: "Jakarta", "sans-serif";
  display: flex;
  align-items: center;
  padding: 30px 0;

  & img {
    width: 134px;
    height: 92px;
  }

  & div {
    margin-left: 20px;
    font-size: 18px;
    text-align: left;
  }
`;

const LabelWrapper = styled(Label)`
  font-family: "Jakarta", "sans-serif";
`

const TextInputWrapper = (props) => <TextInput {...props} $fontFamily={"\"Jakarta\", \"sans-serif\""} />;

const SurchargeAmountWrapper = styled('div')`
  font-family: "Jakarta", "sans-serif";
  color: rgba(84, 84, 84, 0.56);
  margin-top: 8px;
  font-size: 13px;
  padding-left: 5px;
  text-align: center;
`;

export const TrinityKewPaymentForm = () => {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();

  // Hide /trinitykew-pay page for other domains.
  if (window.location.hostname !== 'trinitykew.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  const searchParams = new URLSearchParams(useLocation().search);
  const [billAmount, setBillAmount] = useState(searchParams.get('Amount') || '');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [userEmail, setUserEmail] = useState(searchParams.get('Email') || '');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [accountId, setAccountId] = useState(searchParams.get('AccountID') || '');
  const [accountIDError, setAccountIDError] = useState(false);
  const [familyName, setFamilyName] = useState(searchParams.get('FamilyName') || '');
  const [familyNameError, setFamilyNameError] = useState(false);
  const [userContactPhone, setUserContactPhone] = useState(searchParams.get('Phone') || '');
  const tillPaymentInlineFormRef = useRef(null);
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const persistedTransactionId = usePersistedTransactionId();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(PAYMENT_METHOD_ALIPAY);

  useEffect(() => {
    if (paymentResult !== '') setSelectedPaymentMethod(PAYMENT_METHOD_INITIAL);
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const envParam = new URLSearchParams(useLocation().search).get('env');

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  const handleAccountIDChange = (value) => {
    setAccountId(value);
    setAccountIDError(false);
  };

  const handleFamilyNameChange = (value) => {
    setFamilyName(value);
    setFamilyNameError(false);
  };


  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const createPaymentOrderAndNavigateForAlipay = async (data) => {
    window.location.href = data.redirect_url;
  };

  const getPaymentEndpoint = (selectedPayment) => {
    switch (selectedPayment) {
      case 'wechatpay':
        return '/api/wechat/create-payment';
      case PAYMENT_METHOD_ALIPAY:
        return '/api/alipay/create-payment';
      case 'alipay-dana':
        return '/api/alipay-dana/create-payment';
      default:
        return '';
    }
  }

  const adjustCreatePaymentEndpoint = (endpoint) => {
    if (window.location.hostname === 'www.vidatennis.com.au') return `https://vida.oligo.com.au${endpoint}`;
    return endpoint;
  }

  const createPaymentOrderAndNavigate = async () => {
    if (!billAmount || isNaN(parseFloat(billAmount)) || parseFloat(billAmount) <= 0) {
      setBillAmountError(true);
      return;
    }

    if (!accountId) {
      setAccountIDError(true);
      return;
    }

    if (!familyName) {
      setFamilyNameError(true);
      return;
    }

    setBillAmountDisabled(true);

    if (selectedPaymentMethod === PAYMENT_METHOD_INITIAL) {
      setError('Please select a payment method.');
      return;
    }

    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
      // Validate ReCAPTCHA
      await verifyRecaptcha(recaptchaRef, setError);
    }

    const extraPaymentParams = {
      extraParams: {
        EFN: familyName, // EXTRA_FamilyName
        ECP: userContactPhone, // EXTRA_ContactPhone
      }
    }

    try {
      if (selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD) {
        await tillPaymentInlineFormRef.current.paymentSubmit(setPaymentResult, '/api/alipay-credit-card/create-payment-with-token', extraPaymentParams);
      } else {
        showLoading();

        const createPaymentEndpoint = getPaymentEndpoint(selectedPaymentMethod);
        const createPaymentEndpointWithAdjustedHost = adjustCreatePaymentEndpoint(createPaymentEndpoint);
        const response = await fetch(`${createPaymentEndpointWithAdjustedHost}?env=${envParam}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            billAmount,
            billReference: accountId,
            userEmail,
            persistedTransactionId,
            ...extraPaymentParams,
          }),
        });
        const data = await response.json();
        if (data.return_code === 'SUCCESS') {
          if (selectedPaymentMethod === PAYMENT_METHOD_ALIPAY || selectedPaymentMethod === 'alipay-dana') {
            // hideLoading('Payment submit successful!', 'success');
            await createPaymentOrderAndNavigateForAlipay(data);
          }
        } else {
          hideLoading('Payment creation failed.', 'error');
        }
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    try {
      await createPaymentOrderAndNavigate();
    } catch (err) {
      setError('Payment validation request sending failed');
      console.error(err);
    }
  };

  return (
    <FormContainer>
      <HeroLogo style={{ display: "block", backgroundColor:"#0F2707" }} logo={getPageHeroLogo()} redirectPath='/trinitykew-pay' />
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>{ getPageTitle() }</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <LabelWrapper isRequired>Debtor ID</LabelWrapper>
              <TextInputWrapper
                placeholder=""
                value={accountId || ''}
                onChange={handleAccountIDChange}
                error={accountIDError}
              />
            </FormItemContainer>
            <FormItemContainer>
              <LabelWrapper isRequired>Family Name</LabelWrapper>
              <TextInputWrapper
                placeholder=""
                value={familyName || ''}
                onChange={handleFamilyNameChange}
                error={familyNameError}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <LabelWrapper isRequired>Payment Amount</LabelWrapper>
              <TextInputWrapper
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer>
              <LabelWrapper>Contact Telephone No.</LabelWrapper>
              <TextInputWrapper
                placeholder=""
                value={userContactPhone || ''}
                onChange={setUserContactPhone}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer>
              <LabelWrapper>Receipt Email</LabelWrapper>
              <TextInputWrapper
                placeholder=""
                value={userEmail || ''}
                onChange={setUserEmail}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>

          <div>
            <LabelWrapper>Payment Method</LabelWrapper>

            <PaymentOptionList>
              {/*<PaymentOption*/}
              {/*  onClick={handlePaymentMethodChange(PAYMENT_METHOD_CREDIT_CARD)}*/}
              {/*  selected={selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD}*/}
              {/*  borderColor={getThemeColor()}*/}
              {/*>*/}
              {/*  <PaymentOptionImage src={CreditCardPaymentImage} />*/}
              {/*  <PaymentOptionText>Card</PaymentOptionText>*/}
              {/*</PaymentOption>*/}
              <PaymentOption
                onClick={handlePaymentMethodChange(PAYMENT_METHOD_ALIPAY)}
                selected={selectedPaymentMethod === PAYMENT_METHOD_ALIPAY}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={AliPayPlusPaymentImage} />
                <PaymentOptionText>Alipay</PaymentOptionText>
              </PaymentOption>
            </PaymentOptionList>

            { selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD &&
              <SurchargeAmountWrapper>Surcharge: Visa or Mastercard 1.0%, AMEX 1.5%</SurchargeAmountWrapper>
            }
            { selectedPaymentMethod === PAYMENT_METHOD_ALIPAY &&
              <SurchargeAmountWrapper>Alipay Surcharge of 1.10%. Daily transfer limit of $60,000</SurchargeAmountWrapper>
            }

            <PaymentDetails>
              {/*<PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD}>*/}
              {/*  <TillPaymentInlineForm ref={tillPaymentInlineFormRef}*/}
              {/*                         billAmount={billAmount}*/}
              {/*                         billReference={accountId}*/}
              {/*                         userEmail={userEmail}*/}
              {/*                         initLoadingFinishCallback={() => getDefaultPaymentMethod() && setSelectedPaymentMethod(getDefaultPaymentMethod())}*/}
              {/*                         creditCardImage={getCreditCardPaymentImage()}*/}
              {/*  />*/}
              {/*</PaymentHiddenWrapper>*/}
              <PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_ALIPAY}>
                <PaymentContent>
                  <img src={ContinueToPayImage} />
                  <div>Select ‘Continue to pay’ and you will be redirected to Alipay to complete payment.</div>
                </PaymentContent>
              </PaymentHiddenWrapper>
            </PaymentDetails>

            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }

            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }

            <Button $themeColor={getThemeColor()} $fontFamily="Jakarta" onClick={handlePayBill} block>
              Continue to pay
            </Button>
          </div>

        </Stack>
      </Stack>
      <PrivacyPolicy />
      <Footer />
    </FormContainer>
  );
};
